import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, History, Undo2, Trash2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import ImportRetailerTagsDialog from '@/components/ImportRetailerTagsDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

import { API, errorText, rollbackImportBatch } from '@/utils/api';

const DIVISIONS = ["Arvind Tresca DTR", "Arvind Tresca WS", "GG DTR", "GG WS", "TAS"];

const IMPORT_TYPES = [
  { value: 'sales', label: 'Sales Report', description: 'For revenue tracking, growth analysis, incentives', color: 'blue' },
  { value: 'outstanding', label: 'Outstanding Report', description: 'For payment tracking, ageing, collections', color: 'amber' }
];

export default function Import() {
  const { user } = useOutletContext();
  const [uploading, setUploading] = useState(false);
  const [retailerPrompt, setRetailerPrompt] = useState(null); // { data, fileName, resolve } while the "For" picker is open
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState('');
  const [importType, setImportType] = useState('outstanding'); // sales | outstanding | orders | orders_gg
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  // Order-upload metadata (order types only)
  const [orderTakers, setOrderTakers] = useState([]);
  const [orderPlaces, setOrderPlaces] = useState([]);
  const [importTaker, setImportTaker] = useState('');
  const [importPlace, setImportPlace] = useState('');

  const canImportOrders = user?.role !== 'sales_member' || user?.can_import_orders;
  const isOrderType = importType === 'orders' || importType === 'orders_gg';
  const importTypes = [
    ...IMPORT_TYPES,
    ...(canImportOrders ? [
      { value: 'orders', label: 'Order Copy (Arvind)', description: 'Customer orders — Arvind format', color: 'indigo' },
      { value: 'orders_gg', label: 'GG Order Copy', description: 'GG orders — Excel (flat or block)', color: 'cyan' },
    ] : []),
  ];
  
  // Dialog states
  const [clearDataOpen, setClearDataOpen] = useState(false);
  const [clearDataConfirmText, setClearDataConfirmText] = useState('');
  const [clearing, setClearing] = useState(false);
  const [rollbackBatch, setRollbackBatch] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);

  useEffect(() => {
    fetchImportHistory();
    // Order taker + place options (independent fetches so one failure can't
    // blank the other).
    axios.get(`${API}/order-takers`, { withCredentials: true })
      .then(t => setOrderTakers([...(t.data?.order_takers || []), ...(t.data?.company_takers || [])]))
      .catch(e => console.error('Failed to load order takers', e));
    axios.get(`${API}/config/order-places`, { withCredentials: true })
      .then(p => setOrderPlaces(p.data?.order_places || []))
      .catch(e => console.error('Failed to load order places', e));
  }, []);

  const fetchImportHistory = async () => {
    try {
      const response = await axios.get(`${API}/import/history`, {
        withCredentials: true
      });
      setImportHistory(response.data.imports || []);
    } catch (error) {
      console.error('Failed to fetch import history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };
  
  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    
    if (!selectedDivision) {
      toast.error('Please select a division for this import');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('division', selectedDivision);
    // Order imports carry taker + place (stamped on every line server-side),
    // and ask "who is each wholesaler order for?" before writing anything.
    if (isOrderType) {
      formData.append('ask_retailers', '1');
      if (importTaker) {
        formData.append('order_taker_id', importTaker);
        const tk = orderTakers.find(t => t.employee_id === importTaker);
        formData.append('order_taker_name', tk?.name || '');
      }
      if (importPlace) formData.append('order_place', importPlace);
    }

    // Use correct endpoint based on import type
    const endpoint = importType === 'sales' ? '/import/sales'
      : importType === 'orders' ? '/import/orders'
      : importType === 'orders_gg' ? '/import/orders-gg'
      : '/import/outstanding';

    try {
      let response = await axios.post(
        `${API}${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 300000  // 5 minutes max
        }
      );

      // Duplicate-order guard: the server imported NOTHING and returned the
      // clashing order numbers with existing vs incoming quantities. Ask the
      // user to decide, then re-send the same file with their choice.
      if (response.data?.status === 'duplicate_order_nos') {
        const lines = (response.data.conflicts || [])
          .map(c => `${c.order_no} (${c.shop_name || '—'}): existing ${c.existing_units} units → file has ${c.incoming_units} units`)
          .join('\n');
        const override = window.confirm(
          `These order numbers already exist in ${selectedDivision}:\n\n${lines}\n\n` +
          `OK = OVERRIDE (replace stored quantities with this file's)\n` +
          `Cancel = decide next about keeping them`);
        let action = null;
        if (override) {
          action = 'override';
        } else if (window.confirm('Keep the existing orders untouched and import only the NEW order numbers from this file?\n\nOK = import new only\nCancel = import nothing')) {
          action = 'skip';
        }
        if (!action) {
          toast('Import cancelled — nothing was changed');
          return;
        }
        formData.append('conflict_action', action);
        response = await axios.post(`${API}${endpoint}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 300000,
        });
      }

      // Wholesaler orders in the file: the server wrote nothing and listed
      // them; collect the "For" answers and re-send the same file.
      if (response.data?.status === 'retailer_tags_needed') {
        const tags = await new Promise(resolve => setRetailerPrompt({ data: response.data, fileName: file.name, resolve }));
        if (!tags) {
          toast('Import cancelled — nothing was imported');
          return;
        }
        formData.append('retailer_tags', JSON.stringify(tags));
        response = await axios.post(`${API}${endpoint}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 300000,
        });
      }

      setResult({ ...response.data, importType });
      
      const summary = response.data.summary || {};
      if (importType === 'sales') {
        toast.success(`Sales import complete: ${summary.new_sales || 0} new, ${summary.updated_sales || 0} updated`);
      } else if (isOrderType) {
        toast.success(response.data.message || `Order import: ${response.data.imported || 0} new, ${response.data.updated || 0} updated`);
      } else {
        toast.success(`Outstanding import: ${summary.new_entries || 0} new, ${summary.updated_entries || 0} updated, ${summary.auto_settled || 0} settled`);
      }
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
      // Refresh import history
      fetchImportHistory();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(errorText(error) || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClearAllData = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API}/data/clear-all`, {
        params: { confirm: clearDataConfirmText },
        withCredentials: true,
      });
      toast.success(`All data cleared! Deleted: ${response.data.deleted.customers} customers, ${response.data.deleted.transactions} transactions`);
      setResult(null);
      setFile(null);
      setClearDataOpen(false);
      setClearDataConfirmText('');
      fetchImportHistory();
    } catch (error) {
      console.error('Clear data failed:', error);
      toast.error(errorText(error) || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  const handleRollback = async () => {
    if (!rollbackBatch) return;
    setRollingBack(true);
    try {
      const res = await rollbackImportBatch(axios, rollbackBatch.batch_id, rollbackBatch.original_name);
      if (res) {
        toast.success(res.message || 'Rollback complete');
        setRollbackBatch(null);
        fetchImportHistory();
      }
    } catch (error) {
      console.error('Rollback failed:', error);
      toast.error(errorText(error) || 'Rollback failed');
    } finally {
      setRollingBack(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      rolled_back: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };
  
  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <ImportRetailerTagsDialog prompt={retailerPrompt?.data || null} fileName={retailerPrompt?.fileName}
        onDone={(tags) => { const p = retailerPrompt; setRetailerPrompt(null); if (p) p.resolve(tags); }} />
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground mb-1">
            Import Data
          </h1>
          <p className="text-muted-foreground text-sm">Upload sales, outstanding, and order reports from Excel files</p>
        </div>
        {user?.role === 'admin' && (
          <Button 
            variant="destructive" 
            onClick={() => setClearDataOpen(true)}
            data-testid="clear-all-data-btn"
          >
            <Trash2 size={16} className="mr-2" />
            Clear All Data
          </Button>
        )}
      </div>
      
      {/* Upload Section */}
      <Card className="border-border/60 shadow-sm mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-heading font-semibold flex items-center gap-2">
            <FileSpreadsheet className="text-primary" size={24} />
            Upload Excel File
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Import Type Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Select Report Type *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {importTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      importType === type.value
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="importType"
                      value={type.value}
                      checked={importType === type.value}
                      onChange={(e) => { setImportType(e.target.value); setResult(null); }}
                      className="mt-1"
                    />
                    <div>
                      <span className={`font-semibold ${importType === type.value ? 'text-indigo-800' : 'text-gray-800'}`}>
                        {type.label}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Expected Format Info — only for the financial 5-column reports */}
            {!isOrderType && (
            <div className={`p-4 border rounded-lg ${importType === 'sales' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
              <h3 className={`font-semibold mb-2 ${importType === 'sales' ? 'text-blue-900' : 'text-amber-900'}`}>
                Expected Format (5 columns):
              </h3>
              <div className="grid grid-cols-5 gap-2 text-sm">
                <div className="bg-white p-2 rounded border text-center font-medium">Account (Code)</div>
                <div className="bg-white p-2 rounded border text-center font-medium">Name</div>
                <div className="bg-white p-2 rounded border text-center font-medium">Reference/Invoice</div>
                <div className="bg-white p-2 rounded border text-center font-medium">Date</div>
                <div className="bg-white p-2 rounded border text-center font-medium">Amount</div>
              </div>
              <p className={`text-xs mt-2 ${importType === 'sales' ? 'text-blue-700' : 'text-amber-700'}`}>
                {importType === 'sales' ? (
                  <><strong>Sales Report:</strong> Fixed invoice amounts for revenue tracking. Only positive amounts are imported.</>
                ) : (
                  <><strong>Outstanding Report:</strong> Current balances. Entries not in new report will be auto-settled. Positive = Debit, Negative = Credit.</>
                )}
              </p>
            </div>
            )}

            {/* Order metadata — taker + place (order imports only) */}
            {isOrderType && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-indigo-200 bg-indigo-50/40 rounded-lg">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Order taken by</Label>
                  <Select value={importTaker} onValueChange={setImportTaker}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {orderTakers.some(t => t.kind !== 'company') && (
                        <SelectGroup>
                          <SelectLabel className="text-[11px] font-bold text-indigo-600">Our team</SelectLabel>
                          {orderTakers.filter(t => t.kind !== 'company').map(t => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                        </SelectGroup>
                      )}
                      {orderTakers.some(t => t.kind === 'company') && (
                        <SelectGroup>
                          <SelectLabel className="text-[11px] font-bold text-amber-600">Company / principal employee</SelectLabel>
                          {orderTakers.filter(t => t.kind === 'company').map(t => <SelectItem key={t.employee_id} value={t.employee_id}>{t.name}</SelectItem>)}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Place of order</Label>
                  <Select value={importPlace} onValueChange={setImportPlace}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {orderPlaces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">Applied to every order line in this upload. Editable later from Order Summary.</p>
                </div>
              </div>
            )}

            {/* Division Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Division *</Label>
              <Select value={selectedDivision} onValueChange={setSelectedDivision}>
                <SelectTrigger className="w-full md:w-64" data-testid="division-select">
                  <SelectValue placeholder="Select division..." />
                </SelectTrigger>
                <SelectContent>
                  {DIVISIONS.map((div) => (
                    <SelectItem key={div} value={div}>{div}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* File Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Excel File</Label>
              <div className="flex flex-col md:flex-row gap-4">
                <Input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="flex-1"
                  data-testid="file-input"
                />
                <Button
                  onClick={handleUpload}
                  disabled={!file || !selectedDivision || uploading}
                  className="w-full md:w-auto"
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} className="mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
              {file && (
                <p className="text-sm text-muted-foreground mt-2">
                  Selected: <span className="font-medium">{file.name}</span>
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Upload Result */}
      {result && (
        <Card className={`border-2 mb-6 ${result.status === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {result.status === 'success' ? (
                <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
              ) : (
                <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
              )}
              <div className="flex-1">
                <h3 className={`font-semibold ${result.status === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                  {result.message}
                </h3>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">New Entries:</span>
                    <span className="ml-2 font-semibold">{result.summary?.new_entries || result.imported || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated:</span>
                    <span className="ml-2 font-semibold">{result.summary?.updated_entries || result.updated || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Auto-Settled:</span>
                    <span className="ml-2 font-semibold text-green-700">{result.summary?.auto_settled || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">New Customers:</span>
                    <span className="ml-2 font-semibold">{result.summary?.customers_created || result.customers_created || 0}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total in Report:</span>
                    <span className="ml-2 font-semibold">{result.summary?.total_in_report || '-'}</span>
                  </div>
                </div>
                {(result.summary?.auto_settled || 0) > 0 && (
                  <div className="mt-3 p-3 bg-green-100 rounded-lg">
                    <p className="text-green-800 font-medium">
                      ✓ {result.summary?.auto_settled} entries not in this report were marked as PAID/SETTLED
                    </p>
                  </div>
                )}
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-100 rounded-lg">
                    <p className="text-red-800 font-medium mb-1">Errors ({result.errors.length}):</p>
                    <ul className="text-xs text-red-700 list-disc list-inside max-h-32 overflow-y-auto">
                      {result.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import History */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-heading font-semibold flex items-center gap-2">
            <History className="text-primary" size={24} />
            Import History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : importHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No imports yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Division</TableHead>
                    <TableHead className="text-center">New</TableHead>
                    <TableHead className="text-center">Updated</TableHead>
                    <TableHead className="text-center">Settled</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importHistory.map((imp) => (
                    <TableRow key={imp.batch_id} className={imp.status === 'rolled_back' ? 'opacity-50' : ''}>
                      <TableCell className="text-sm">{formatDate(imp.uploaded_at)}</TableCell>
                      <TableCell className="text-sm font-medium max-w-[200px] truncate" title={imp.original_name}>
                        {imp.original_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{imp.division || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-center">{imp.records_imported || 0}</TableCell>
                      <TableCell className="text-center">{imp.records_updated || 0}</TableCell>
                      <TableCell className="text-center text-green-700">{imp.records_settled || 0}</TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(imp.status)}>
                          {imp.status || 'unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {imp.status === 'completed' && user?.role === 'admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRollbackBatch(imp)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            data-testid={`rollback-${imp.batch_id}`}
                          >
                            <Undo2 size={16} className="mr-1" />
                            Rollback
                          </Button>
                        )}
                        {imp.status === 'rolled_back' && (
                          <span className="text-xs text-muted-foreground">
                            Rolled back {imp.rolled_back_at ? formatDate(imp.rolled_back_at) : ''}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clear All Data Confirmation Dialog */}
      <AlertDialog open={clearDataOpen} onOpenChange={setClearDataOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Clear All Data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete ALL customers, transactions, and ledger entries. 
              This action cannot be undone.
              <br /><br />
              Type <strong>DELETE</strong> to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={clearDataConfirmText}
            onChange={(e) => setClearDataConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="mt-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAllData}
              disabled={clearDataConfirmText !== 'DELETE' || clearing}
              className="bg-red-600 hover:bg-red-700"
            >
              {clearing ? 'Clearing...' : 'Clear All Data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rollback Confirmation Dialog */}
      <AlertDialog open={!!rollbackBatch} onOpenChange={() => setRollbackBatch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <Undo2 size={24} />
              Rollback Import?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all records created by this import:
              <br /><br />
              <strong>File:</strong> {rollbackBatch?.original_name}
              <br />
              <strong>Division:</strong> {rollbackBatch?.division}
              <br />
              <strong>Records:</strong> {rollbackBatch?.records_imported || 0} new, {rollbackBatch?.records_updated || 0} updated
              <br /><br />
              Next you'll see exactly what will be removed, kept or restored before anything changes.
              Removed rows are soft-deleted (recoverable).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRollback}
              disabled={rollingBack}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {rollingBack ? 'Rolling back...' : 'Confirm Rollback'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
